import classnames from "classnames";
import React from "react";
import { useCallback, useEffect, useState } from "react";
import _ from "underscore";
import { Avatar } from "../../../core/atoms/atoms";
import { DataType, TextEditor, ToolbarOptions } from "../textEditor";

import "./messageEditor.scss";

interface Props {
  /**
   * The user sending the message.
   */
  user: IUser;

  /**
   * The message to edit. Used to initialise the editor.
   */
  message: string | undefined;

  saveDraftMessage: (message: string) => void;

  /**
   * Action to run when the send button is pressed.
   */
  sendMessage: (message: string) => Promise<void> | void;

  /**
   * Optional prop to intervene when the send button is clicked. Useful for adding a confirmation dialog.
   */
  sendWrap?: (sendMessage: Props["sendMessage"]) => void;

  sendLabel?: string;

  toolbarOptions: ToolbarOptions[];

  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Message editor implemented with Slate.js
 *
 * TODO (features not transferred from Alloy-based editor)
 * - tips for LTOs
 * - image uploads
 */
export const MessageEditor = (props: Props) => {
  // Remount editor when messages are sent, to reset initialValue.
  const [sendCount, setSendCount] = useState(0);

  const [sending, setSending] = useState(false);

  // Proxy changes in state before sending to Redux, for simpler debounce implementation.
  // Debounce saves to local storage - each time this happens, Redux updates and it's expensive
  const [draft, setDraft] = useState(props.message);
  const saveDraftDebounced = useCallback(_.debounce(props.saveDraftMessage, 2500), [sendCount]);
  useEffect(() => { saveDraftDebounced(draft); }, [draft]); // Trigger debounced save when draft changes

  const doSend = async () => {
    if (sending) {
      return;
    }

    setSending(true);
    await saveDraftDebounced.cancel(); // Cancel any imminent saves

    try {
      await props.sendMessage(draft);

      setDraft(null); // Reset local state
      props.saveDraftMessage(null); // Clear localStorage
      setSendCount(sendCount + 1); // Force remount of Slate component
    }
    catch (e) {
      await props.saveDraftMessage(draft); // Don't lose data on failure
      // TODO error UX

      // * Temporary fix: See #2672
      if (props.sendWrap) {
        throw e; // rethrow so outer try/catch with catch exception. If there is NO outer try/catch, this will just throw an error
      }
    }
    finally {
      setTimeout(() => setSending(false), 1000);
    }
  };

  const onSendClick = () => {
    if (props.sendWrap) {
      props.sendWrap(doSend);
    }
    else {
      doSend();
    }
  };

  return (
    <div className={classnames("message-editor", props.className)}>
      <div className="message-editor__title">
        New message
      </div>

      <div className={classnames("message-editor__bubble", {
        "message-editor__bubble--sending": sending,
      })}>
        <TextEditor
          key={sendCount}
          dataType={DataType.Html}
          initialValue={props.message}
          onChange={setDraft}
          toolbarOptions={props.toolbarOptions}
          toolbarBoundingElem=".message-editor"
          readOnly={props.disabled}
          placeholder={props.placeholder}
        />
      </div>

      <div className="message-editor__actions">
        <button
          className={classnames("primary-button", "message-editor__send", {
            "message-editor__send--sending": sending,
          })}
          onClick={onSendClick}
          disabled={props.disabled || !draft}>
          {sending ? "Sending..." : (props.sendLabel || "Send")}
        </button>
      </div>

      <div className="message-editor__sender is-hidden-mobile">
        <Avatar source={props.user.image ? props.user.image.thumbnailUrl : null} />
      </div>
    </div>
  );
};
